import React, { useState, useEffect } from 'react';
import { 
  Star, 
  MessageSquare, 
  CheckCircle, 
  AlertCircle,
  Eye,
  Calendar,
  User
} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const ReviewSection = ({ parent, selectedStudent, companyId, onReviewSubmitted }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [existingReviews, setExistingReviews] = useState([]);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: ''
  });
  const [hoveredRating, setHoveredRating] = useState(0);

  useEffect(() => {
    if (companyId && selectedStudent && parent) {
      checkReviewPermission();
      loadExistingReviews();
    }
  }, [companyId, selectedStudent, parent]);

  const checkReviewPermission = async () => {
    try {
      setLoading(true);
      const canSubmit = await dataHelpers.canParentReview(
        parent.id, 
        selectedStudent.id, 
        companyId
      );
      setCanReview(canSubmit);
    } catch (error) {
      console.error('Error checking review permission:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingReviews = async () => {
    try {
      const reviews = await dataHelpers.getStudentReviews(selectedStudent.id, companyId);
      setExistingReviews(reviews || []);
    } catch (error) {
      console.error('Error loading existing reviews:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!reviewData.rating || reviewData.rating < 1 || reviewData.rating > 5) {
      toast.error('Por favor, selecione uma avaliação de 1 a 5 estrelas');
      return;
    }

    if (!reviewData.comment.trim()) {
      toast.error('Por favor, escreva um comentário sobre sua experiência');
      return;
    }

    try {
      setSubmitting(true);
      
      const reviewPayload = {
        parent_id: parent.id,
        parent_name: parent.name,
        student_id: selectedStudent.id,
        rating: reviewData.rating,
        comment: reviewData.comment.trim()
      };

      await dataHelpers.createReview(companyId, reviewPayload);
      
      // Limpar formulário
      setReviewData({ rating: 0, comment: '' });
      setCanReview(false);
      
      // Recarregar avaliações existentes
      await loadExistingReviews();
      
      // Notificar componente pai
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Erro ao enviar avaliação. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRatingClick = (rating) => {
    setReviewData(prev => ({ ...prev, rating }));
  };

  const handleCommentChange = (e) => {
    setReviewData(prev => ({ ...prev, comment: e.target.value }));
  };

  const renderStars = (rating, isInteractive = false) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isActive = starValue <= (isInteractive ? (hoveredRating || rating) : rating);
      
      if (isInteractive) {
        return (
          <button
            key={index}
            type="button"
            onMouseEnter={() => setHoveredRating(starValue)}
            onMouseLeave={() => setHoveredRating(0)}
            onClick={() => handleRatingClick(starValue)}
            className={`p-1 transition-colors duration-200 ${
              isActive 
                ? 'text-yellow-400 hover:text-yellow-500' 
                : 'text-gray-300 hover:text-yellow-300'
            }`}
          >
            <Star className={`w-8 h-8 ${isActive ? 'fill-current' : ''}`} />
          </button>
        );
      }
      
      return (
        <Star
          key={index}
          className={`w-4 h-4 ${
            isActive ? 'text-yellow-400 fill-current' : 'text-gray-300'
          }`}
        />
      );
    });
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Muito Insatisfeito',
      2: 'Insatisfeito', 
      3: 'Regular',
      4: 'Satisfeito',
      5: 'Muito Satisfeito'
    };
    return labels[rating] || '';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-8 space-y-6">
      {/* Nova Avaliação */}
      {canReview ? (
        <Card className="p-8">
          <div className="text-center mb-6">
            <Star className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Avalie nossos serviços
            </h2>
            <p className="text-gray-600">
              Sua opinião é muito importante para nós! Avalie sua experiência com 
              os serviços da cantina para o aluno <strong>{selectedStudent.name}</strong>.
            </p>
          </div>

          <form onSubmit={handleSubmitReview} className="space-y-6">
            {/* Sistema de Estrelas */}
            <div className="text-center">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Como você avalia nossos serviços? *
              </label>
              <div className="flex justify-center space-x-2 mb-4">
                {renderStars(reviewData.rating, true)}
              </div>
              
              {(hoveredRating || reviewData.rating) > 0 && (
                <p className="text-sm font-medium text-gray-700">
                  {getRatingLabel(hoveredRating || reviewData.rating)}
                </p>
              )}
            </div>

            {/* Comentário */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conte-nos sobre sua experiência *
              </label>
              <textarea
                value={reviewData.comment}
                onChange={handleCommentChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                placeholder="Descreva sua experiência com nossos serviços, o que gostou e sugestões de melhoria..."
                required
                maxLength={500}
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-sm text-gray-500">
                  Seja específico e construtivo em seu feedback
                </p>
                <p className="text-xs text-gray-400">
                  {reviewData.comment.length}/500
                </p>
              </div>
            </div>

            {/* Botão de Envio */}
            <div className="text-center">
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                disabled={!reviewData.rating || !reviewData.comment.trim()}
                className="px-8 py-3"
              >
                {submitting ? 'Enviando Avaliação...' : 'Enviar Avaliação'}
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Card className="p-8 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Obrigado por sua avaliação!
          </h3>
          <p className="text-gray-600">
            Você já avaliou nossos serviços para o aluno <strong>{selectedStudent.name}</strong>.
            Sua opinião é muito importante para nós!
          </p>
        </Card>
      )}

      {/* Avaliações Anteriores */}
      {existingReviews.length > 0 && (
        <Card>
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Eye className="w-5 h-5 mr-2" />
              Avaliações do Aluno ({existingReviews.length})
            </h3>
          </div>
          
          <div className="divide-y divide-gray-200">
            {existingReviews.map((review) => (
              <div key={review.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="font-medium text-gray-900">
                        {review.parent?.name || review.parent_name}
                      </span>
                      <div className="flex items-center ml-4">
                        {renderStars(review.rating)}
                        <span className="ml-2 text-sm text-gray-600">
                          ({review.rating}/5)
                        </span>
                      </div>
                    </div>
                    
                    {review.comment && (
                      <blockquote className="text-gray-700 italic pl-4 border-l-4 border-gray-200 mb-3">
                        "{review.comment}"
                      </blockquote>
                    )}
                    
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar className="w-3 h-3 mr-1" />
                      {formatDate(review.created_at)}
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      review.rating >= 4 
                        ? 'bg-green-100 text-green-800' 
                        : review.rating >= 3 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {review.rating >= 4 ? 'Positiva' : review.rating >= 3 ? 'Neutra' : 'Crítica'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Informações Adicionais */}
      <Card className="p-6 bg-blue-50 border border-blue-200">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Sobre as avaliações:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Cada responsável pode avaliar uma vez por aluno</li>
              <li>Sua avaliação ajuda a melhorar nossos serviços</li>
              <li>Todas as avaliações são analisadas pela nossa equipe</li>
              <li>Mantenha o respeito e seja construtivo nos comentários</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ReviewSection;